import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

// ストレージのキー
const STORAGE_KEYS = {
  IDENTIFIER: 'auth_identifier',
  PASSWORD: 'auth_password',
  REMEMBER_ME: 'auth_remember_me',
} as const;

/**
 * 認証情報を安全に保存する
 */
export const saveCredentials = async (
  identifier: string,
  password: string,
  rememberMe: boolean = true
): Promise<void> => {
  try {
    if (rememberMe) {
      // expo-secure-storeはWeb環境では動作しない可能性があるため条件分岐
      if (Platform.OS === 'web') {
        // Webの場合はlocalStorageを使用（警告: 本番環境では推奨されません）
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(STORAGE_KEYS.IDENTIFIER, identifier);
          localStorage.setItem(STORAGE_KEYS.PASSWORD, password);
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
        }
      } else {
        // iOS/AndroidではSecureStoreを使用
        await SecureStore.setItemAsync(STORAGE_KEYS.IDENTIFIER, identifier);
        await SecureStore.setItemAsync(STORAGE_KEYS.PASSWORD, password);
        await SecureStore.setItemAsync(STORAGE_KEYS.REMEMBER_ME, 'true');
      }
    } else {
      // ログイン情報を記憶しない場合は削除
      await clearCredentials();
    }
  } catch (error) {
    console.error('認証情報の保存に失敗しました:', error);
    throw error;
  }
};

/**
 * 保存された認証情報を取得する
 */
export const getCredentials = async (): Promise<{
  identifier: string;
  password: string;
} | null> => {
  try {
    let rememberMe: string | null;
    let identifier: string | null;
    let password: string | null;

    if (Platform.OS === 'web') {
      // Web環境でlocalStorageが使用できるか確認
      if (typeof localStorage === 'undefined') {
        return null;
      }
      rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);
      identifier = localStorage.getItem(STORAGE_KEYS.IDENTIFIER);
      password = localStorage.getItem(STORAGE_KEYS.PASSWORD);
    } else {
      rememberMe = await SecureStore.getItemAsync(STORAGE_KEYS.REMEMBER_ME);
      identifier = await SecureStore.getItemAsync(STORAGE_KEYS.IDENTIFIER);
      password = await SecureStore.getItemAsync(STORAGE_KEYS.PASSWORD);
    }

    // ログイン情報を記憶する設定でない場合はnullを返す
    if (rememberMe !== 'true') {
      return null;
    }

    // 認証情報が両方存在する場合のみ返す
    if (identifier && password) {
      return { identifier, password };
    }

    return null;
  } catch (error) {
    console.error('認証情報の取得に失敗しました:', error);
    return null;
  }
};

/**
 * 保存された認証情報を削除する
 */
export const clearCredentials = async (): Promise<void> => {
  try {
    if (Platform.OS === 'web') {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(STORAGE_KEYS.IDENTIFIER);
        localStorage.removeItem(STORAGE_KEYS.PASSWORD);
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
      }
    } else {
      await SecureStore.deleteItemAsync(STORAGE_KEYS.IDENTIFIER);
      await SecureStore.deleteItemAsync(STORAGE_KEYS.PASSWORD);
      await SecureStore.deleteItemAsync(STORAGE_KEYS.REMEMBER_ME);
    }
  } catch (error) {
    console.error('認証情報の削除に失敗しました:', error);
    // 削除の失敗は致命的ではないため、エラーを投げない
  }
};

/**
 * ログイン情報を記憶する設定を取得する
 */
export const getRememberMe = async (): Promise<boolean> => {
  try {
    let rememberMe: string | null;

    if (Platform.OS === 'web') {
      if (typeof localStorage === 'undefined') {
        return false;
      }
      rememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME);
    } else {
      rememberMe = await SecureStore.getItemAsync(STORAGE_KEYS.REMEMBER_ME);
    }

    return rememberMe === 'true';
  } catch (error) {
    console.error('ログイン記憶設定の取得に失敗しました:', error);
    return false;
  }
};
